import { toCents } from '@nossorachao/utils';
import { weekdayLabel } from '@nossorachao/utils';
import { Calendar, ChevronRight, MapPin, Plus, Ticket, Users, Volleyball as Ball } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, EmptyState, Field, Input, Skeleton } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAcceptInvite, useCreateGroup, useGroups } from '@/lib/queries';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function GroupsPage() {
  const { data: groups, isLoading } = useGroups();
  const [modal, setModal] = useState<'none' | 'create' | 'invite'>('none');

  return (
    <>
      <PageHeader
        title="Peladas"
        subtitle="Cada grupo tem seus participantes, partidas e caixa próprios."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" icon={Ticket} onClick={() => setModal('invite')}>
              Tenho convite
            </Button>
            <Button icon={Plus} onClick={() => setModal('create')}>
              Criar pelada
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[0, 1, 2, 3].map((index) => (
            <Card key={index}>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="mt-3 h-4 w-28" />
            </Card>
          ))}
        </div>
      ) : !groups?.length ? (
        <Card>
          <EmptyState
            icon={Ball}
            title="Você ainda não está em nenhuma pelada"
            description="Crie a sua ou entre com um convite que alguém te mandou."
            action={
              <div className="flex gap-2">
                <Button icon={Plus} onClick={() => setModal('create')}>
                  Criar pelada
                </Button>
                <Button variant="secondary" icon={Ticket} onClick={() => setModal('invite')}>
                  Tenho convite
                </Button>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((group) => (
            <Link key={group.id} to={`/grupos/${group.id}`}>
              <Card className="h-full transition-colors hover:border-line-strong">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-base font-bold">{group.name}</h3>
                      {group.myRole !== 'MEMBER' ? (
                        <Badge tone="brand">{group.myRole === 'OWNER' ? 'Dono' : 'Admin'}</Badge>
                      ) : null}
                    </div>

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-ink-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" aria-hidden />
                        {weekdayLabel(group.weekday, true)} · {group.startTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" aria-hidden />
                        {group.memberCount}
                      </span>
                    </div>

                    {group.venueName ? (
                      <p className="mt-1 flex items-center gap-1 truncate text-[13px] text-ink-3">
                        <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {group.venueName}
                      </p>
                    ) : null}

                    {group.nextGame ? (
                      <div className="mt-3 rounded-lg bg-brand-soft px-3 py-2">
                        <div className="flex items-center justify-between text-[13px] font-semibold text-brand">
                          <span>
                            Próxima:{' '}
                            {new Intl.DateTimeFormat('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            }).format(new Date(group.nextGame.scheduledAt))}
                          </span>
                          <span className="tabular">
                            {group.nextGame.confirmedCount}/{group.nextGame.maxPlayers}
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {modal === 'create' ? <CreateGroupModal onClose={() => setModal('none')} /> : null}
      {modal === 'invite' ? <AcceptInviteModal onClose={() => setModal('none')} /> : null}
    </>
  );
}

// =============================================================================
// Modais
// =============================================================================

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md animate-fade-in rounded-card bg-surface p-6 shadow-pop">
        <h2 className="mb-5 text-lg font-bold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * Criação da pelada.
 *
 * Pede só o essencial para o grupo existir; convidados, avaliações e janela de
 * presença têm padrão razoável e ficam nas configurações. Vinte campos na
 * primeira tela é o jeito mais rápido de perder quem acabou de chegar.
 */
function CreateGroupModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const create = useCreateGroup();

  const [form, setForm] = useState({
    name: '',
    venueName: '',
    startTime: '20:00',
    maxPlayers: '16',
    playersPerTeam: '8',
    monthlyFee: '',
  });
  const [weekday, setWeekday] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '', _: '' }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});

    try {
      const group = await create.mutateAsync({
        name: form.name.trim(),
        venueName: form.venueName.trim() || null,
        weekday,
        startTime: form.startTime,
        maxPlayers: Number(form.maxPlayers),
        playersPerTeam: Number(form.playersPerTeam),
        monthlyFeeCents: form.monthlyFee ? toCents(form.monthlyFee) : 0,
        dueDayOfMonth: 5,
        timezone: 'America/Sao_Paulo',
      });

      navigate(`/grupos/${group.id}`);
    } catch (caught) {
      setErrors(
        caught instanceof ApiError && caught.details?.length
          ? caught.fieldErrors
          : { _: caught instanceof ApiError ? caught.message : 'Não foi possível criar.' },
      );
    }
  }

  const fitsTwoTeams = Number(form.maxPlayers) >= Number(form.playersPerTeam) * 2;
  const canSubmit =
    form.name.trim().length >= 3 &&
    /^([01]\d|2[0-3]):[0-5]\d$/.test(form.startTime) &&
    fitsTwoTeams;

  return (
    <Modal title="Criar pelada" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nome da pelada" error={errors.name ?? errors._}>
          <Input
            placeholder="Ex.: Pelada do Douglas"
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>

        <Field label="Local" hint="Onde vocês jogam">
          <Input
            placeholder="Ex.: Arena Society Vila Nova"
            value={form.venueName}
            onChange={(event) => update('venueName', event.target.value)}
          />
        </Field>

        <Field label="Dia da semana">
          <div className="flex gap-1">
            {WEEKDAYS.map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => setWeekday(index)}
                className={`flex-1 rounded-lg border py-2 text-[13px] font-semibold transition-colors ${
                  weekday === index
                    ? 'border-brand bg-brand text-on-brand'
                    : 'border-line bg-sunken text-ink-2 hover:border-line-strong'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Horário" error={errors.startTime}>
            <Input
              type="time"
              value={form.startTime}
              onChange={(event) => update('startTime', event.target.value)}
              invalid={Boolean(errors.startTime)}
            />
          </Field>

          <Field label="Mensalidade" hint="Vazio = não cobra">
            <Input
              inputMode="decimal"
              placeholder="80,00"
              value={form.monthlyFee}
              onChange={(event) => update('monthlyFee', event.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Máx. de jogadores" error={errors.maxPlayers}>
            <Input
              inputMode="numeric"
              value={form.maxPlayers}
              onChange={(event) => update('maxPlayers', event.target.value.replace(/\D/g, ''))}
              invalid={!fitsTwoTeams}
            />
          </Field>

          <Field label="Por time">
            <Input
              inputMode="numeric"
              value={form.playersPerTeam}
              onChange={(event) => update('playersPerTeam', event.target.value.replace(/\D/g, ''))}
            />
          </Field>
        </div>

        {!fitsTwoTeams ? (
          <p className="text-[13px] text-warn">
            O máximo precisa comportar dois times completos ({Number(form.playersPerTeam) * 2}{' '}
            jogadores).
          </p>
        ) : null}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" full loading={create.isPending} disabled={!canSubmit}>
            Criar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AcceptInviteModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const accept = useAcceptInvite();
  const [code, setCode] = useState('');

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      const result = await accept.mutateAsync(code.trim());
      onClose();
      if (!result.pendingApproval) navigate(`/grupos/${result.groupId}`);
    } catch {
      // O erro aparece no campo.
    }
  }

  return (
    <Modal title="Entrar com convite" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Código do convite"
          hint="Está no final do link que te enviaram"
          error={accept.error instanceof ApiError ? accept.error.message : undefined}
        >
          <Input
            placeholder="Cole o código aqui"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            invalid={Boolean(accept.error)}
            autoFocus
          />
        </Field>

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            full
            loading={accept.isPending}
            disabled={code.trim().length < 8}
          >
            Entrar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { Modal };
